import { AsYouType, parsePhoneNumberFromString } from "libphonenumber-js";

import { PHONE_COUNTRIES, PhoneCountry } from "./phone-countries";

const SUPPORTED_COUNTRIES: string[] = PHONE_COUNTRIES.map(
	(country) => country.code
);

const digitsOnly = (value: string): string => value.replace(/\D/g, "");

/**
 * Mexico dropped the mobile "1" after +52 in 2019, but people still type it
 * (+52 1 55 1234 5678). Strip it so the number validates.
 */
const stripLegacyMexicanMobilePrefix = (value: string): string => {
	const trimmed = value.trim();
	const digits = digitsOnly(trimmed);
	if (trimmed.startsWith("+") && digits.length === 13 && /^521/.test(digits)) {
		return `+52${digits.slice(3)}`;
	}
	return trimmed;
};

/**
 * Turn whatever the user typed into an E.164 number (e.g. "+14155552671"),
 * or `null` when it is not a valid US, Canadian or Mexican number.
 *
 * A leading "+" wins over the selected country, so pasting "+52 55 1234 5678"
 * while "US" is selected still works.
 */
export const normalizePhoneNumber = (
	input: string,
	country: PhoneCountry
): string | null => {
	if (!input || digitsOnly(input) === "") return null;
	const value = stripLegacyMexicanMobilePrefix(input);
	const parsed = parsePhoneNumberFromString(value, country);
	if (!parsed || !parsed.isValid()) return null;
	if (!parsed.country || !SUPPORTED_COUNTRIES.includes(parsed.country)) {
		return null;
	}
	return parsed.number;
};

/** Friendly as-you-type formatting for the phone field. */
export const formatPhoneNumberInput = (
	input: string,
	country: PhoneCountry
): string => {
	if (!input) return "";
	return new AsYouType(country).input(input);
};

/** Readable version of an E.164 number, e.g. "+1 415 555 2671". */
export const formatPhoneNumberForDisplay = (e164: string): string => {
	const parsed = parsePhoneNumberFromString(e164);
	return parsed ? parsed.formatInternational() : e164;
};
